/**
 * This is an experimental form utility.
 *
 * It has been redesigned from the previous `useForm` utility, however, it has
 * not yet replaced it. I hope to eventually replace the exiting utility with
 * this one, but for now please keep using the old utility.
 *
 * This new utility has been created in response to the usage of the old
 * utility. I hope it allows greater flexibility, cleaner code and a simpler
 * final implementation.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { isNull, isUndefined, merge } from 'lodash';
import _isEmpty from 'lodash/isEmpty';

const FormContext = createContext();

function processDefaultValue(value) {
  if (typeof value === 'function') {
    return value();
  } else {
    return value;
  }
}

export function useForm(id, defaultValue) {
  const form = useContext(FormContext);

  if (!form) {
    throw new Error(
      'FormContext not found, did you forget to wrap this component in a <Form />?'
    );
  }

  const { readonly, errors, state, setState, setDirty, updateFieldError } =
    form;

  // Intercept user-driven state updates to set the dirty status
  const changeState = useCallback(
    (data, { doSetDirty = true } = {}) => {
      if (doSetDirty) {
        setDirty(true);
      }

      setState(data);
    },
    [setState, setDirty]
  );

  const value = useMemo(() => state[id], [state, id]);
  const error = useMemo(() => errors[id], [errors, id, state]);
  const setValue = useCallback(
    (newValue, opts) =>
      changeState(
        (state) => ({
          ...state,
          [id]: typeof newValue === 'function' ? newValue(state[id]) : newValue,
        }),
        opts
      ),
    [changeState, id]
  );

  const handleChange = useCallback(
    (e, opts) => changeState({ ...state, [id]: e.target.value }, opts),
    [state, changeState, id]
  );

  useEffect(() => {
    if (readonly || !isUndefined(state[id]) || isUndefined(defaultValue)) {
      return;
    }

    const value = processDefaultValue(defaultValue);
    setState({ ...state, [id]: value });

    // we only want this to run once, thus the empty array
    // @eslint-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return {
    ...form,
    setState: changeState,
    error,
    value,
    setValue,
    handleChange,
    updateFieldError,
  };
}

/**
 * Converts the "raw" state object to a nested parsed object.
 * ie. { 'foo.bar': true, 'baz': 42 } -> { foo: { bar: true }, baz: 42 }
 */
function parseState(raw) {
  let state = {};

  Object.keys(raw).forEach((key) => {
    let value = raw[key];
    if (value === null) return;

    const obj = key
      // "foo.bar.baz" -> ["foo", "bar", "baz"]
      .split(/\./g)
      // ["foo", "bar", "baz"] -> { foo: { bar: { baz: value } } }
      .reduceRight((obj, x) => ({ [x]: obj }), value);

    state = merge(obj, state);
  });

  return state;
}

/**
 * Converts the nested parsed object to a "raw" state object.
 * ie. { foo: { bar: true }, baz: 42 } -> { 'foo.bar': true, 'baz': 42 }
 *
 * Any key begining with a '!' is excluded.
 * ie. { "!foo": { bar: true }, baz: 42 } -> { foo: { bar: true }, baz: 42 }
 */
function readState(raw) {
  function inner(currentNode, target, flattenedKey) {
    Object.entries(currentNode).forEach(([key, value]) => {
      const newKey = flattenedKey ? flattenedKey + '.' + key : key;

      if (typeof value === 'object' && value !== null && !key.startsWith('!')) {
        inner(value, target, newKey);
      } else {
        if (key.startsWith('!')) {
          target[
            flattenedKey ? flattenedKey + '.' + key.slice(1) : key.slice(1)
          ] = value;
        } else if (target[newKey] === undefined) {
          target[newKey] = value;
        }
      }
    });
  }

  let state = {};
  inner(raw ?? {}, state, null);
  return state;
}

function prepareState(raw) {
  let state = {};

  Object.entries(raw).forEach(([key, value]) => {
    state[key] = value;

    if (value instanceof Set) {
      state[key] = [...value];
    }
  });

  return state;
}

export function useFormApi() {
  const [api, update] = useState({});

  api.update = update;
  return api;
}

export default function Form({
  onSubmit,
  onCancel,
  onError,
  schema,
  readonly,
  initialState,
  children,
  showErrors = true,
  api,

  // For external state management
  state: externalState,
  setState: externalSetState,
}) {
  const [internalState, internalSetState] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const state = externalState ?? internalState;
  const setState = externalSetState ?? internalSetState;

  const handleSubmit = useCallback(
    (rawState) => {
      const state = parseState(prepareState(rawState));

      setLoading(true);
      setErrors({});

      schema
        .validate(state, { abortEarly: false })
        .then(() => onSubmit(state))
        .then(() => setLoading(false))
        .catch((err) => {
          const errors_raw = err.inner
            .filter(({ errors }) => errors.length > 0)
            .map(({ errors, path }) => [errors[0], path]);

          const errors = {};
          for (let i = 0, e = errors_raw.length; i < e; i++) {
            const [error, path] = errors_raw[i];
            errors[path] = error;
          }

          setErrors(errors);
          setLoading(false);

          if (typeof onError === 'function') {
            onError(state);
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSubmit]
  );

  const updateFieldError = useCallback(
    (field, err) =>
      setErrors((prv) => {
        if (err) {
          return {
            ...errors,
            [field]: err,
          };
        } else {
          delete prv[field];
          return prv;
        }
      }),
    [errors, setErrors]
  );

  useEffect(() => {
    if (!isNull(initialState) && !isUndefined(initialState)) {
      setState(readState(initialState));
    }
  }, [initialState]);

  useEffect(() => {
    if (api?.update) {
      api.update({
        isLoading,
        state,
        errors,
        readonly,
        dirty,
      });
    }
  }, [api?.update, isLoading, state, errors, readonly, dirty]);

  return (
    <FormContext.Provider
      value={{
        isLoading,
        onSubmit: handleSubmit,
        onCancel,
        state,
        setState,
        errors: showErrors ? errors : {},
        readonly,
        setDirty,
        dirty,
        updateFieldError,
      }}
    >
      {typeof children === 'function'
        ? children(state, _isEmpty(errors), errors)
        : children}
    </FormContext.Provider>
  );
}
